// frontend/src/pages/ParentDashboard.tsx
// MinikCoder Veli Paneli — Keskin, Kurumsal, Gerçek Veriye Dayalı Gelişim & Takip Sistemi

import { useState, useEffect } from 'react';
import { useParentDashboard } from '../hooks/useParentDashboard';
import { ChildSelector } from '../components/parent/ChildSelector';
import { ChildHeaderCard } from '../components/parent/ChildHeaderCard';
import { ParentStatsGrid } from '../components/parent/ParentStatsGrid';
import { CourseProgressSection } from '../components/parent/CourseProgressSection';
import { ParentHeatmap } from '../components/parent/ParentHeatmap';
import { ParentAIInsightCard } from '../components/parent/ParentAIInsightCard';
import { ReportTabBar, PeriodSummaryCard } from '../components/parent/WeeklyReportCard';
import { AiTopicsCard } from '../components/parent/AiTopicsCard';
import {
  TimeTrendAreaChart,
  CategoryDonutChart,
  PeriodBarChart,
  DayPatternChart,
  QuizScoreDonut,
} from '../components/parent/ParentCharts';
import { LinkChildForm } from '../components/auth/LinkChildForm';
import { authService } from '../services/auth.service';
import { ChildPlanInfo, ChildLeagueInfo } from '../types/parent.types';
import {
  IconUsers,
  IconPrinter,
  IconTrendUp,
  IconActivity,
  IconPieChart,
  IconRefresh,
} from '../components/parent/ParentIcons';
import '../styles/parent.css';

const API_BASE =
  import.meta.env.PROD &&
  (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)
    ? ''
    : import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

async function fetchChildPlan(childId: string): Promise<ChildPlanInfo> {
  const token = await authService.getAccessToken();
  const res = await fetch(`${API_BASE}/api/parent/children/${childId}/plan`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Plan bilgisi alınamadı.');
  return res.json();
}

async function fetchChildLeague(childId: string): Promise<ChildLeagueInfo> {
  const token = await authService.getAccessToken();
  const res = await fetch(`${API_BASE}/api/parent/children/${childId}/league`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Lig bilgisi alınamadı.');
  return res.json();
}

const PLAN_BADGE: Record<string, { label: string; color: string }> = {
  free:    { label: 'Ücretsiz', color: '#64748B' },
  basic:   { label: 'Temel Plan', color: '#0EA5E9' },
  premium: { label: 'Premium Plan', color: '#F59E0B' },
  pro:     { label: 'Pro Plan', color: '#8B5CF6' },
};

export function ParentDashboardPage() {
  const {
    children,
    childrenLoading,
    childrenError,
    selectedChildId,
    setSelectedChildId,
    overview,
    overviewLoading,
    overviewError,
    insight,
    insightLoading,
    loadInsight,
    reloadChildren,
    activeTab,
    setActiveTab,
    activityReport,
    activityLoading,
    aiTopics,
    aiTopicsLoading,
  } = useParentDashboard();

  const [showLinkForm, setShowLinkForm] = useState(false);
  const [planInfo, setPlanInfo] = useState<ChildPlanInfo | null>(null);
  const [leagueInfo, setLeagueInfo] = useState<ChildLeagueInfo | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!selectedChildId) return;
    setPlanInfo(null);
    setLeagueInfo(null);
    Promise.all([
      fetchChildPlan(selectedChildId).catch(() => null),
      fetchChildLeague(selectedChildId).catch(() => null),
    ]).then(([plan, league]) => {
      setPlanInfo(plan);
      setLeagueInfo(league);
    });
  }, [selectedChildId]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await reloadChildren();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  if (childrenLoading) {
    return (
      <div className="pcd-full-loading">
        <div className="pcd-spinner" />
        <p>Veli analiz paneli hazırlanıyor...</p>
      </div>
    );
  }

  if (childrenError) {
    return <div className="pcd-error-banner">{childrenError}</div>;
  }

  const planKey = planInfo?.planName?.toLowerCase() ?? 'free';
  const planBadge = PLAN_BADGE[planKey] ?? PLAN_BADGE.free;

  return (
    <div className="pcd-page">
      {/* ─── HEADER ──────────────────────────────────── */}
      <header className="pcd-page-header">
        <div className="pcd-header-title-row">
          <div className="pcd-header-icon-wrap">
            <IconUsers size={22} />
          </div>
          <div>
            <h1 className="pcd-page-title">Veli Gelişim & Takip Paneli</h1>
            <p className="pcd-page-subtitle">
              Çocuğunuzun gerçek çalışma verileri, sınav başarıları ve pedagojik analizleri
            </p>
          </div>
        </div>

        <div className="pcd-header-actions-wrap">
          <button
            className={`pcd-refresh-btn ${isRefreshing ? 'spinning' : ''}`}
            onClick={handleManualRefresh}
            title="Verileri Yenile"
          >
            <IconRefresh size={15} />
            <span>Verileri Güncelle</span>
          </button>

          {planInfo?.planName && (
            <div className="pcd-plan-badge" style={{ borderColor: planBadge.color, color: planBadge.color }}>
              <span className="pcd-plan-dot" style={{ background: planBadge.color }} />
              {planBadge.label}
              {planInfo.expiresAt && (
                <span className="pcd-plan-expiry">
                  — {new Date(planInfo.expiresAt).toLocaleDateString('tr-TR', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
          )}
        </div>
      </header>

      {/* ─── BOŞ DURUM (HESAP BAĞLI DEĞİLSE) ───────────────── */}
      {children.length === 0 ? (
        <div className="pcd-empty-state">
          <div className="pcd-empty-icon-wrap">
            <IconUsers size={44} />
          </div>
          <h3>Henüz Bağlı Öğrenci Hesabı Bulunmuyor</h3>
          <p>
            Çocuğunuzun <strong>Profil Ayarları</strong> ekranındaki{' '}
            <strong style={{ color: '#0EA5E9' }}>8 Haneli Öğrenci Kodu</strong>'nu girerek hesabını anında bağlayabilirsiniz.
          </p>
          <div className="pcd-link-form-wrap">
            <LinkChildForm onSuccess={reloadChildren} />
          </div>
        </div>
      ) : (
        <>
          {/* ─── ÇOCUK SEÇİCİ KONTROLÜ ─────────────────────── */}
          <ChildSelector
            children={children}
            selectedId={selectedChildId}
            onSelect={setSelectedChildId}
            onAddChild={() => setShowLinkForm((v) => !v)}
          />

          {showLinkForm && (
            <div className="pcd-link-form-inline">
              <LinkChildForm
                onSuccess={() => {
                  reloadChildren();
                  setShowLinkForm(false);
                }}
              />
            </div>
          )}

          {overviewLoading && (
            <div className="pcd-overview-loading">
              <div className="pcd-spinner" />
              <span>Güncel veriler hesaplanıyor...</span>
            </div>
          )}

          {overviewError && <div className="pcd-error-banner">{overviewError}</div>}

          {overview && (
            <>
              {/* ─── 1. ÖĞRENCİ KİMLİK & LİG KARTI ─────────────────── */}
              <ChildHeaderCard
                child={overview.profile}
                rank={leagueInfo?.rank}
                totalPlayers={leagueInfo?.totalPlayers}
                completedCoursesCount={overview.completedCoursesCount}
                inProgressCoursesCount={overview.inProgressCoursesCount}
                completedLessonsCount={overview.completedLessonsCount}
              />

              {/* ─── 2. DÖNEM SEÇİCİ TAB & ÖZET RAPOR ───────────── */}
              <div className="pcd-report-section">
                <div className="pcd-report-section-header">
                  <div className="pcd-section-indicator" style={{ background: '#0EA5E9' }} />
                  <h3 className="pcd-section-title">
                    <IconTrendUp size={18} style={{ marginRight: 8 }} />
                    Dönemsel Performans & Aktivite Özeti
                  </h3>
                </div>
                <ReportTabBar activeTab={activeTab} onChange={setActiveTab} />
                <PeriodSummaryCard report={activityReport} isLoading={activityLoading} />
              </div>

              {/* ─── 3. 8 ADET KESKİN KPI METRİK KARTI ──────────── */}
              <ParentStatsGrid report={activityReport} overview={overview} isLoading={activityLoading} />

              {/* ─── 4. GRAFİKLER BÖLÜMÜ: 2x2 YAN YANA KOMPAKT DÜZEN ─── */}
              <div className="pcd-charts-grid-two">
                {/* 1. Zaman & Süre Trend Grafiği */}
                <TimeTrendAreaChart
                  timeTrend={activityReport?.timeTrend ?? []}
                  title={`${activityReport?.label ?? 'Aktivite'} Zaman Trendi`}
                  isLoading={activityLoading}
                />

                {/* 2. Faaliyet Çubuk Dağılım Grafiği */}
                <PeriodBarChart report={activityReport} />
              </div>

              <div className="pcd-charts-grid-two">
                {/* 3. Haftalık Gün Yoğunluğu */}
                <DayPatternChart days={overview.heatmap} />

                {/* 4. Quiz Başarısı & Branş Dağılımı (Kombine Kart) */}
                <div className="pcd-donuts-paired-card pcd-sharp-card">
                  <div className="pcd-chart-header">
                    <div className="pcd-chart-title">Sınav & Alan Yetkinliği</div>
                    <div className="pcd-chart-sub">Genel başarı ve kategori dağılımı</div>
                  </div>
                  <div className="pcd-donuts-paired-body">
                    <QuizScoreDonut
                      score={overview.averageQuizScore}
                      totalQuizzes={overview.totalQuizzes}
                      passedCount={overview.passedQuizzesCount}
                      embedded
                    />
                    <CategoryDonutChart
                      categories={overview.categoryDistribution ?? []}
                      title="Alan Dağılımı"
                      embedded
                    />
                  </div>
                </div>
              </div>


              {/* ─── 6. DERSLER — GÜÇLÜ YÖNLER & GELİŞİM ALANLARI ─── */}
              <div className="pcd-two-col">
                <CourseProgressSection
                  title="Güçlü Olduğu Alanlar (%60 ve Üzeri)"
                  items={overview.strengths}
                  emptyText="Henüz %60 üzerinde tamamlanmış kurs kaydı bulunmuyor."
                  variant="strength"
                  childId={overview.profile.id}
                />
                <CourseProgressSection
                  title="Pekiştirilmesi Gereken Alanlar"
                  items={overview.growthAreas}
                  emptyText="Tebrikler! Öğrencinin geride kaldığı bir gelişim alanı bulunmuyor."
                  variant="growth"
                  childId={overview.profile.id}
                />
              </div>

              {/* ─── 7. ÇOK KAYNAKLI AKTİVİTE HARİTASI ───────────── */}
              <ParentHeatmap days={overview.heatmap} />

              {/* ─── 8. AI MENTOR SORULARI & SORU SIKLIĞI ─────────── */}
              <AiTopicsCard data={aiTopics} isLoading={aiTopicsLoading} />

              {/* ─── 9. PEDAGOJİK AI GELİŞİM DEĞERLENDİRMESİ ──────── */}
              <ParentAIInsightCard
                insight={insight}
                isLoading={insightLoading}
                onLoad={loadInsight}
              />

              {/* ─── 10. RESMİ HTML / PDF RAPOR YAZDIRMA ─────────── */}
              <div className="pcd-pdf-row">
                <a
                  href={`${API_BASE}/api/parent/children/${overview.profile.id}/report-html`}
                  target="_blank"
                  rel="noreferrer"
                  className="pcd-pdf-btn"
                >
                  <IconPrinter size={16} />
                  <span>Resmî Gelişim Raporunu Yazdır / PDF İndir</span>
                </a>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
